// Misc
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import capitalize from 'capitalize';
import classNames from 'classnames';
import { connect } from 'react-redux';

// Utilities
import { isPendingStab } from 'utils/stab';

// Decorators
import withRequest from 'decorators/withRequest';

// Actions
import { fetchSubscriptionPlans } from 'actions/subscriptionPlan';

// Atoms
import Link from 'atoms/Link';
import Tooltip from 'material-ui/Tooltip';
import Container from 'atoms/Container';
import Typography from 'material-ui/Typography';
import ButtonBase from 'material-ui/ButtonBase';
import Grid from 'material-ui/Grid';
import Paper from 'material-ui/Paper';
import Divider from 'material-ui/Divider';
import Collapse from 'material-ui/transitions/Collapse';
import Svg from 'atoms/Svg';
import Hidden from 'material-ui/Hidden';
import { CircularProgress } from 'material-ui/Progress';
// import Parallax from 'atoms/Parallax';

// Components
import Helmet from 'components/Helmet';
import Header from 'components/Header';
import Footer from 'components/Footer';

// Icons
import planSparkSymbol from 'assets/icons/plan-spark.svg';
import planLiftOffSymbol from 'assets/icons/plan-lift-off.svg';
import planFlightSymbol from 'assets/icons/plan-flight.svg';
import CheckIcon from 'material-ui-icons/Check';
import ExpandMoreIcon from 'material-ui-icons/ExpandMore';
import ExpandLessIcon from 'material-ui-icons/ExpandLess';

// Styles
import styles from './styles';

const faqItemGroups = [
  [
    {
      question: 'How do I get started?',
      answer: 'Start by selecting one of our subscription plans. Once you click "Get Started" you will be directed to a quick onboarding process. We\'re available 24-7 to help with everything from getting you set-up and showing you how to use all of our features to suggesting which new restaurants to try out. Seriously, we\'re that friendly.'
    },
    {
      question: 'What happens if I want to cancel?',
      answer: 'Please email us anytime at hello@tewtor.com if you’re not happy with your plan.'
    },
    {
      question: 'How many tutors can I have?',
      answer: 'As many as you want! In your subscription page, simply opt to add more tutors to your plan.'
    }
  ],
  [
    {
      question: 'How does billing work?',
      answer: 'Your credit card is charged monthly depending on your plan.'
    },
    {
      question: 'Which plan is right for me?',
      answer: 'We encourage our schools to start off with our Lift-Off plan as it includes all of our key features and offers a good starting point. However, you can change your plan at any time.'
    },
    {
      question: 'Monthly vs. annual plans',
      answer: 'Subscribe to a monthly plan if you\'re not quite yet ready to commit to a full year. Our annual plans are billed at a discounted rate and still let you switch between the plans depending on your needs.'
    }
  ]
];

const mapStateToProps = ({ subscriptionPlan }) => ({ subscriptionPlan });
const mapDispatchToProps = { fetchSubscriptionPlansAction: fetchSubscriptionPlans };

@withRequest
@connect(mapStateToProps, mapDispatchToProps)
@withStyles(styles)
export default class PricingPage extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    subscriptionPlan: PropTypes.object.isRequired,
    fetchSubscriptionPlansAction: PropTypes.func.isRequired,
    observeRequest: PropTypes.func.isRequired,
    getObservedRequest: PropTypes.func.isRequired,
  };

  state = {
    billingType: 'annual',
    expandedFaqItems: {}
  };

  async componentDidMount() {
    const { subscriptionPlan, observeRequest, fetchSubscriptionPlansAction } = this.props;
    if (subscriptionPlan.collection === undefined) {
      try {
        await observeRequest(cancelToken => fetchSubscriptionPlansAction(cancelToken));
      } catch (error) {
        // continue regardless of error
      }
    }
  }

  render() {
    const { classes, subscriptionPlan, getObservedRequest } = this.props;
    const request = getObservedRequest();

    return (
      <div className={classes.root}>
        <Helmet />
        <Header activeMenuLink="/pricing" />

        <Container>
          <div className={classes.billingType}>
            { ['monthly', 'annual'].map(billingType => (
              <ButtonBase
                key={billingType}
                className={classNames(classes.billingTypeButton, {
                  [classes.billingTypeButtonActive]: billingType === this.state.billingType
                })}
                onClick={() => this.setState({ billingType })}
              >
                { capitalize(billingType) }
              </ButtonBase>
            )) }
            <div className={classes.billingTypeOr}>or</div>
          </div>
        </Container>

        <Container>
          { request.isPending() &&
            <div className={classes.plansProgress}>
              <CircularProgress size={50} />
            </div>
          }
          { !!subscriptionPlan.collection &&
            !isPendingStab(subscriptionPlan.collection) &&
            <Grid
              className={classes.plans}
              container
              justify="center"
              align="stretch"
              spacing={40}
            >
              { subscriptionPlan.collection.map(plan => (
                <Grid
                  key={plan._id}
                  item
                  className={classes.plan}
                  xs={12}
                  md={12 / subscriptionPlan.collection.length}
                >
                  <Paper className={classes.planInner}>
                    <div className={classes.planIcon}>
                      <Svg
                        symbol={({
                          Spark: planSparkSymbol,
                          'Lift-off': planLiftOffSymbol,
                          Flight: planFlightSymbol
                        })[plan.description.title]}
                      />
                    </div>
                    <Typography className={classes.planTitle}>
                      { plan.description.title }
                    </Typography>
                    <Typography className={classes.planPrice}>
                      ${ parseFloat(plan.price[this.state.billingType].amount) }
                    </Typography>
                    <Typography color="secondary" className={classes.planPriceHint}>
                      { plan.price[this.state.billingType].currency } per month
                      { this.state.billingType === 'annual' && ', billed annually' }
                    </Typography>
                    <Typography className={classes.planSchoolsLabel}>
                      { plan.description.schoolCaption }
                    </Typography>
                    <ButtonBase
                      to="/onboarding"
                      component={Link}
                      className={classes.planButton}
                    >
                      { plan.description.hasFreeTrial ?
                        'Try for free' :
                        'Get started'
                      }
                    </ButtonBase>
                    <Divider className={classes.planDivider} />
                    <Typography className={classes.planDescription}>
                      { plan.description.tagline }
                    </Typography>
                    <Divider className={classes.planDivider} />
                    <Typography className={classes.planFeaturesTitle}>
                      { plan.description.featuresCaption }
                    </Typography>
                    <div className={classes.planFeatures}>
                      { plan.description.features.map((feature) => {
                        const [caption, tooltip] = feature.split('*');
                        return (
                          <Typography key={caption} className={classes.planFeature} component="div">
                            <CheckIcon className={classes.planFeatureCheck} />
                            { tooltip ? (
                              <Tooltip title={tooltip}>
                                <span>
                                  { caption }*
                                </span>
                              </Tooltip>) : caption
                            }
                          </Typography>
                        );
                      }) }
                    </div>
                  </Paper>
                </Grid>
              )) }
            </Grid>
          }
        </Container>

        <div className={classes.purpleStripe}>
          <Container>
            <ButtonBase
              className={classes.purpleStripeButton}
              component="a"
              href="mailto:hello@tewtor.com"
            >
              Request a demo
            </ButtonBase>
          </Container>
        </div>

        <Container className={classes.faq}>
          <Typography className={classes.faqTitle}>
            Frequently asked questions
          </Typography>
          <Grid
            container
            className={classes.faqItemGroups}
            spacing={24}
          >
            { faqItemGroups.map((faqItemGroup, faqItemGroupIndex) => {
              return (
                <Grid
                  key={faqItemGroupIndex} // eslint-disable-line
                  item
                  xs={12}
                  md={12 / faqItemGroups.length}
                >
                  { faqItemGroup.map((faqItem, faqItemIndex) => {
                    const key = `${faqItemGroupIndex}-${faqItemIndex}`;
                    const { expandedFaqItems } = this.state;
                    const expanded = expandedFaqItems[key];
                    return (
                      <div key={key} className={classes.faqItem}>
                        <button
                          className={classes.faqItemTitle}
                          onClick={() => {
                            if (expanded) {
                              this.setState({
                                expandedFaqItems: { ...expandedFaqItems, [key]: false }
                              });
                            } else {
                              this.setState({
                                expandedFaqItems: { ...expandedFaqItems, [key]: true }
                              });
                            }
                          }}
                        >
                          { faqItem.question }
                          { expanded ?
                            <ExpandLessIcon className={classes.faqItemTitleArrow} /> :
                            <ExpandMoreIcon className={classes.faqItemTitleArrow} />
                          }
                        </button>
                        <Divider />
                        <Collapse in={expanded} transitionDuration="auto">
                          <Typography className={classes.faqItemAnswer}>
                            { faqItem.answer }
                          </Typography>
                        </Collapse>
                      </div>
                    );
                  }) }
                </Grid>
              );
            }) }
          </Grid>
        </Container>

        <Hidden mdDown>
          <div className={classes.footerRocket} />
        </Hidden>

        <Footer containerClassName={classes.footerContainer} />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/Pricing/index.js